// profileSchemas.ts
import { z } from 'zod';

const minCharNoSpaces = (min: number) => (val: string) => val.replace(/\s+/g, '').length >= min;
// const noMultipleSpaces = (val: string) => !/\s{2,}/.test(val);
const isImageFile = (file: File) => file.type.startsWith('image/');
const allowedAudioMimeTypes = [
  'audio/mpeg',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/ogg',
  'audio/aac',
  'audio/x-aac',
  'audio/m4a',
  'audio/x-m4a',
  'audio/mp4',
];
const allowedAudioExtensions = ['.mp3', '.wav', '.ogg', '.aac', '.m4a'];
const hasAllowedAudioExtension = (file: File) =>
  allowedAudioExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));
const isAllowedAudioFile = (file: File) =>
  (!file.type || allowedAudioMimeTypes.includes(file.type)) && hasAllowedAudioExtension(file);

const requiredTextField = (label: string, min: number, max: number) =>
  z
    .string()
    .min(1, `${label} is required`)
    .max(max, `${label} must be at most ${max} characters`)
    // .refine(noMultipleSpaces, {
    //   message: 'Only one space is allowed between words',
    // })
    .transform((val) => val.trim())
    .refine(minCharNoSpaces(min), {
      message: `${label} must be at least ${min} characters (excluding spaces)`,
    });

const optionalTextField = (label: string, min = 1, max = 500) =>
  z
    .string()
    .max(max, `${label} must be at most ${max} characters`)
    // .refine((val) => !val || noMultipleSpaces(val), {
    //   message: 'Only one space is allowed between words',
    // })
    .transform((val) => val.trim())
    .refine((val) => !val || minCharNoSpaces(min)(val), {
      message: `${label} must be at least ${min} characters (excluding spaces)`,
    })
    .optional()
    .or(z.literal(''));

const optionalUrlField = z
  .string()
  .trim()
  .url('Invalid URL format')
  .max(500, 'Max 500 characters')
  .optional()
  .or(z.literal(''));

const UrlField = z.string().trim().url('Invalid URL format').max(500, 'Max 500 characters');

const optionalImageField = z
  .any()
  .refine(
    (value) =>
      value === undefined ||
      value === null ||
      value === '' ||
      typeof value === 'string' ||
      value instanceof File,
    {
      message: 'Invalid image file',
    }
  )
  .refine(
    (value) =>
      value === undefined ||
      value === null ||
      value === '' ||
      typeof value === 'string' ||
      isImageFile(value),
    {
      message: 'Only image files are allowed',
    }
  )
  .refine(
    (value) =>
      value === undefined ||
      value === null ||
      value === '' ||
      value instanceof File ||
      z.string().url().safeParse(value).success,
    {
      message: 'Invalid URL format',
    }
  )
  .optional();

export const profileSchema = z.object({
  firstname: z.string().min(1, 'First name is required'),
  lastname: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email'),
});

export const passwordSchema = z.object({
  currentPassword: z.string().min(6, 'Current password required'),
  newPassword: z.string().min(6, 'Minimum 6 characters'),
});

export const contentSchema = z
  .object({
    title: requiredTextField('Title', 3, 80),
    description: optionalTextField('Description', 5, 1000),

    isLocked: z.boolean().optional(),

    mantra: optionalTextField('Mantra', 5, 1000),
    mantraTranslation: optionalTextField('Mantra translation', 5, 1000),
    mantraPronunciation: optionalTextField('Mantra pronunciation', 5, 1000),
    product: optionalUrlField,
    personalProduct: optionalUrlField,

    mantraIcon: z
      .any()
      .refine((file) => !file || file instanceof File, {
        message: 'Invalid icon file',
      })
      .refine((file) => !file || file.type.startsWith('image/'), {
        message: 'Only image files are allowed for the icon',
      })
      .optional(),
    mantraIconUrl: z.string().optional(),
    embedUrl: optionalUrlField,

    file: z
      .any()
      .refine((file) => !file || file instanceof File, {
        message: 'Invalid audio file',
      })
      .refine((file) => !file || isAllowedAudioFile(file), {
        message: 'Only MP3, WAV, OGG, AAC, or M4A audio files are allowed',
      })
      .optional(),
    videoFile: z
      .any()
      .refine((file) => !file || file instanceof File, {
        message: 'Invalid video file',
      })
      .refine((file) => !file || ['video/mp4', 'video/webm', 'video/ogg'].includes(file.type), {
        message: 'Only MP4, WEBM, or OGG videos are allowed',
      })
      .optional(),

    image: z.any().optional(),
  })

  // audio validation
  .refine(
    (data) => {
      const hasAudio = data.file instanceof File;
      const hasEmbed = !!data.embedUrl?.trim();

      const hasVideo = !data.isLocked && data.videoFile instanceof File;

      return hasAudio || hasEmbed || hasVideo;
    },
    {
      message: 'Audio, video, or embed URL is required',
      path: ['file'],
    }
  )

  // locked content validation
  .superRefine((data, ctx) => {
    if (!data.isLocked && !data.description?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['description'],
        message: 'Description is required',
      });
    }
    if (!data.isLocked) return;

    if (!data.mantra?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mantra'],
        message: 'Mantra is required',
      });
    }

    if (!data.mantraTranslation?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mantraTranslation'],
        message: 'Mantra translation is required',
      });
    }

    if (!data.mantraPronunciation?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mantraPronunciation'],
        message: 'Mantra pronunciation is required',
      });
    }

    // if (!data.mantraIcon) {
    //   ctx.addIssue({
    //     code: z.ZodIssueCode.custom,
    //     path: ['mantraIcon'],
    //     message: 'Mantra icon is required',
    //   });
    // }
    if (!data.mantraIcon && !data.mantraIconUrl) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mantraIcon'],
        message: 'Mantra icon is required',
      });
    }
    // if (!data.product?.trim()) {
    //   ctx.addIssue({
    //     code: z.ZodIssueCode.custom,
    //     path: ['product'],
    //     message: 'Product link is required',
    //   });
    // }
    // if (!data.personalProduct?.trim()) {
    //   ctx.addIssue({
    //     code: z.ZodIssueCode.custom,
    //     path: ['personalProduct'],
    //     message: 'Personal product link is required',
    //   });
    // }
  });

export const createContentSchema = (isLocked: boolean) =>
  z.object({
    title: requiredTextField('Title', 3, 60),
    description: requiredTextField('Description', 5, 400),
    category: z.string().optional(),
    benefits: optionalTextField('Benefits', 3, 400),
    sourceType: optionalTextField('Best time to listen', 3, 400),
    productLink: optionalUrlField,
    personalizedProductLink: optionalUrlField,
    imageUrl: optionalImageField,
    iconUrl: optionalImageField,
    embedUrl: optionalUrlField,
    videoUrl: optionalUrlField,

    sanskritMantra: isLocked
      ? requiredTextField('Sanskrit mantra', 5, 1000)
      : optionalTextField('Sanskrit mantra', 5, 1000),

    englishTranslation: isLocked
      ? requiredTextField('English translation', 5, 1000)
      : optionalTextField('English translation', 5, 1000),

    englishPronunciation: isLocked
      ? requiredTextField('English pronunciation', 5, 1000)
      : optionalTextField('English pronunciation', 5, 1000),
  });
